import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
    History, Calendar, Truck, Users, Camera, ArrowRight, Loader2,
    TrendingUp, Wrench, Fuel, AlertTriangle, Route, LogOut
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const FEED_STYLES = {
    inspection: { icon: Camera, color: 'text-[#1B7EB5]', bg: 'bg-[#1B7EB5]/10' },
    maintenance: { icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-50' },
    fuel: { icon: Fuel, color: 'text-[#8CC774]', bg: 'bg-[#8CC774]/10' },
    damage: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' }
};

export default function HistoryPage() {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);
    const navigate = useNavigate();
    const { language } = useLanguage();
    const el = language === 'el';

    useEffect(() => {
        const fetchOverview = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/history/overview?days=${days}`);
                setOverview(response.data);
            } catch (error) {
                toast.error(el ? 'Αποτυχία φόρτωσης ιστορικού' : 'Failed to fetch history overview');
            } finally {
                setLoading(false);
            }
        };
        fetchOverview();
    }, [days, el]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    const cards = [
        { key: 'inspections', icon: Camera, label: el ? 'Παραλαβές' : 'Check-ins', value: overview?.total_inspections || 0, sub: `${overview?.total_checkouts || 0} ${el ? 'παραδόσεις' : 'check-outs'}`, color: 'text-[#1B7EB5]', bg: 'bg-[#1B7EB5]/10', link: '/app/inspections' },
        { key: 'distance', icon: Route, label: el ? 'Χιλιόμετρα' : 'Distance', value: `${(overview?.total_distance_km || 0).toLocaleString()} km`, sub: `${overview?.unique_vehicles || 0}/${overview?.fleet_size || 0} ${el ? 'οχήματα σε χρήση' : 'vehicles used'}`, color: 'text-[#58A6B4]', bg: 'bg-[#58A6B4]/10', link: '/app/analytics' },
        { key: 'maintenance', icon: Wrench, label: el ? 'Συντηρήσεις' : 'Maintenance', value: overview?.total_maintenance || 0, sub: `€${(overview?.maintenance_cost || 0).toLocaleString()}`, color: 'text-amber-600', bg: 'bg-amber-50', link: '/app/maintenance' },
        { key: 'fuel', icon: Fuel, label: el ? 'Καύσιμα' : 'Fuel', value: overview?.total_fuel_logs || 0, sub: `€${(overview?.fuel_cost || 0).toLocaleString()}`, color: 'text-[#8CC774]', bg: 'bg-[#8CC774]/10', link: '/app/fuel-logs' },
        { key: 'damage', icon: AlertTriangle, label: el ? 'Ζημιές' : 'Damage', value: overview?.total_damage_alerts || 0, sub: el ? 'ειδοποιήσεις AI' : 'AI alerts', color: 'text-red-500', bg: 'bg-red-50', link: '/app/damage-reports' },
        { key: 'drivers', icon: Users, label: el ? 'Ενεργοί Οδηγοί' : 'Active Drivers', value: overview?.unique_drivers || 0, sub: el ? 'στο διάστημα' : 'in period', color: 'text-[#1B7EB5]', bg: 'bg-[#1B7EB5]/10', link: '/app/drivers' }
    ];

    const maxBar = Math.max(1, ...(overview?.daily_activity || []).map(d => d.inspections + d.checkouts + d.maintenance + d.fuel));

    return (
        <div className="space-y-6 animate-fade-in" data-testid="history-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 font-heading flex items-center gap-2">
                        <History className="w-6 h-6 text-[#1B7EB5]" />
                        {el ? 'Ιστορικό Στόλου' : 'Fleet History'}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {el ? 'Συνολική δραστηριότητα από όλες τις καρτέλες' : 'Combined activity from every tab'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{el ? 'Περίοδος:' : 'Period:'}</span>
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900"
                        data-testid="history-period-select"
                    >
                        <option value={7}>{el ? 'Τελευταίες 7 ημέρες' : 'Last 7 days'}</option>
                        <option value={30}>{el ? 'Τελευταίες 30 ημέρες' : 'Last 30 days'}</option>
                        <option value={90}>{el ? 'Τελευταίοι 3 μήνες' : 'Last 3 months'}</option>
                        <option value={365}>{el ? 'Τελευταίος χρόνος' : 'Last year'}</option>
                    </select>
                </div>
            </div>

            {/* Stats linking to the other tabs */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {cards.map(({ key, icon: Icon, label, value, sub, color, bg, link }) => (
                    <button
                        key={key}
                        onClick={() => navigate(link)}
                        className="kpi-card text-left hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid={`history-card-${key}`}
                    >
                        <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center mb-2`}>
                            <Icon className={`w-5 h-5 ${color}`} />
                        </div>
                        <p className="kpi-label">{label}</p>
                        <p className="kpi-value">{value}</p>
                        <p className="text-xs text-slate-500 mt-1">{sub}</p>
                    </button>
                ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Most Active Vehicles */}
                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <Truck className="w-5 h-5 text-[#1B7EB5]" />
                            {el ? 'Πιο ενεργά οχήματα' : 'Most Active Vehicles'}
                        </h3>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/app/vehicles')} className="text-[#1B7EB5] hover:text-[#58A6B4]" data-testid="history-view-all-vehicles">
                            {el ? 'Όλα' : 'View All'} <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {overview?.most_active_vehicles?.length > 0 ? overview.most_active_vehicles.map((vehicle, index) => (
                            <button
                                key={vehicle.vehicle_id}
                                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                                onClick={() => navigate(`/app/vehicles/${vehicle.vehicle_id}`)}
                                data-testid={`vehicle-history-${index}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#1B7EB5]/20 rounded-lg flex items-center justify-center text-[#1B7EB5] font-bold text-sm">#{index + 1}</div>
                                    <div>
                                        <p className="text-slate-800 font-medium text-sm">{vehicle.vehicle_info}</p>
                                        <p className="text-xs text-slate-500">{(vehicle.distance_km || 0).toLocaleString()} km</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[#1B7EB5] font-semibold">{vehicle.checkin_count}</p>
                                    <p className="text-xs text-slate-500">{el ? 'παραλαβές' : 'check-ins'}</p>
                                </div>
                            </button>
                        )) : (
                            <p className="text-slate-500 text-center py-4">{el ? 'Καμία δραστηριότητα οχημάτων' : 'No vehicle activity in this period'}</p>
                        )}
                    </div>
                </div>

                {/* Most Active Drivers */}
                <div className="fleet-card p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-[#58A6B4]" />
                            {el ? 'Πιο ενεργοί οδηγοί' : 'Most Active Drivers'}
                        </h3>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/app/drivers')} className="text-[#1B7EB5] hover:text-[#58A6B4]" data-testid="history-view-all-drivers">
                            {el ? 'Όλοι' : 'View All'} <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {overview?.most_active_drivers?.length > 0 ? overview.most_active_drivers.map((driver, index) => (
                            <button
                                key={driver.driver_id}
                                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left"
                                onClick={() => navigate('/app/drivers')}
                                data-testid={`driver-history-${index}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#58A6B4]/20 rounded-lg flex items-center justify-center text-[#58A6B4] font-bold text-sm">#{index + 1}</div>
                                    <div>
                                        <p className="text-slate-800 font-medium text-sm">{driver.driver_name}</p>
                                        <p className="text-xs text-slate-500">{(driver.distance_km || 0).toLocaleString()} km</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[#58A6B4] font-semibold">{driver.checkin_count}</p>
                                    <p className="text-xs text-slate-500">{el ? 'παραλαβές' : 'check-ins'}</p>
                                </div>
                            </button>
                        )) : (
                            <p className="text-slate-500 text-center py-4">{el ? 'Καμία δραστηριότητα οδηγών' : 'No driver activity in this period'}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Unified activity feed */}
            <div className="fleet-card p-4">
                <h3 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4">
                    <LogOut className="w-5 h-5 text-[#1B7EB5]" />
                    {el ? 'Πρόσφατη δραστηριότητα' : 'Recent Activity'}
                </h3>
                <div className="space-y-2" data-testid="history-activity-feed">
                    {overview?.recent_activity?.length > 0 ? overview.recent_activity.map((item, index) => {
                        const style = FEED_STYLES[item.type] || FEED_STYLES.inspection;
                        const Icon = style.icon;
                        return (
                            <button
                                key={`${item.type}-${index}`}
                                onClick={() => navigate(item.link)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                                data-testid={`history-feed-item-${index}`}
                            >
                                <div className={`w-9 h-9 ${style.bg} rounded-lg flex items-center justify-center shrink-0`}>
                                    <Icon className={`w-4 h-4 ${style.color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-800 truncate">{item.title}</p>
                                    <p className="text-xs text-slate-500 truncate">{el ? item.detail_el : item.detail_en}</p>
                                </div>
                                <span className="text-xs text-slate-400 shrink-0">
                                    {item.date ? new Date(item.date).toLocaleDateString(el ? 'el-GR' : 'en-US') : '—'}
                                </span>
                                <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
                            </button>
                        );
                    }) : (
                        <p className="text-slate-500 text-center py-4">{el ? 'Καμία δραστηριότητα στο διάστημα' : 'No activity in this period'}</p>
                    )}
                </div>
            </div>

            {/* Daily Activity Chart */}
            <div className="fleet-card p-4">
                <h3 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-[#8CC774]" />
                    {el ? 'Ημερήσια δραστηριότητα' : 'Daily Activity'}
                </h3>
                <div className="overflow-x-auto">
                    <div className="flex gap-1 min-w-max pb-2 items-end h-40">
                        {overview?.daily_activity?.map((day) => {
                            const total = day.inspections + day.checkouts + day.maintenance + day.fuel;
                            return (
                                <div key={day.date} className="flex flex-col items-center justify-end h-full">
                                    <div
                                        className="w-6 rounded-t bg-gradient-to-t from-[#1B7EB5] to-[#8CC774] transition-all hover:opacity-80"
                                        style={{ height: `${Math.max(3, (total / maxBar) * 100)}%` }}
                                        title={`${day.date}: ${day.inspections} ${el ? 'παραλαβές' : 'check-ins'}, ${day.checkouts} ${el ? 'παραδόσεις' : 'check-outs'}, ${day.maintenance} ${el ? 'συντηρήσεις' : 'maintenance'}, ${day.fuel} ${el ? 'καύσιμα' : 'fuel'}`}
                                    />
                                    <p className="text-[10px] text-slate-500 mt-1 rotate-45 origin-left w-10">{day.date.slice(5)}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
